import axios from "axios";
import { showGlobalAuthModal } from "../hooks/useAuthModal";
import { emitAuthChanged } from "../utils/authHelpers";

const baseURL = process.env.REACT_APP_BASE_URL || "http://127.0.0.1:8000";
console.log("HTTP baseURL:", baseURL);

const http = axios.create({
  baseURL,
  timeout: 60000,
});

let authModalOpen = false;
const AUTH_MODAL_SESSION_KEY = "auth_modal_shown_once";

const openAuthModalOncePerSession = (message) => {
  const alreadyShown = sessionStorage.getItem(AUTH_MODAL_SESSION_KEY) === "1";

  if (authModalOpen || alreadyShown) return;

  authModalOpen = true;
  sessionStorage.setItem(AUTH_MODAL_SESSION_KEY, "1");

  showGlobalAuthModal(message);

  setTimeout(() => {
    authModalOpen = false;
  }, 1500);
};

const isGuestEndpoint = (url = "") => url.includes("/api/guest/");

const isPublicCustomerEndpoint = (url = "") => {
  return (
    url.includes("/api/customer/shipping-rates") ||
    url.includes("/api/customer/products") ||
    url.includes("/api/customer/categories") ||
    url.includes("/api/customer/banners") ||
    url.includes("/api/customer/vouchers/top") ||
    url.includes("/api/customer/vouchers/shipping") ||
    url.includes("/api/customer/vendors/") ||
    url.includes("/api/customer/auth/google") ||
    url.includes("/api/customer/payments/success") ||
    url.includes("/api/customer/payments/cancel") ||
    url.includes("/api/customer/payments/stripe/success") ||
    url.includes("/api/customer/payments/stripe/cancel") ||
    url.includes("/api/customer/payments/stripe/webhook")
  );
};

http.interceptors.request.use((config) => {
  const authToken = localStorage.getItem("authToken");
  const guestToken = localStorage.getItem("guestToken");
  const url = config.url || "";

  config.headers = config.headers || {};

  if (isGuestEndpoint(url)) {
    if (guestToken) {
      config.headers["X-Guest-Token"] = guestToken;
    }
    delete config.headers.Authorization;
    return config;
  }

  if (isPublicCustomerEndpoint(url)) {
    if (authToken && authToken.trim()) {
      config.headers.Authorization = `Bearer ${authToken.trim()}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  }

  if (authToken && authToken.trim()) {
    config.headers.Authorization = `Bearer ${authToken.trim()}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase();
    const url = error.config?.url || "";

    console.log("HTTP Interceptor - Error caught:", status, method, url);

    if (status === 401) {
      const authToken = localStorage.getItem("authToken");
      const guestToken = localStorage.getItem("guestToken");

      const isUser = !!(authToken && authToken.trim());
      const isGuest = !isUser && !!(guestToken && guestToken.trim());

      if (isPublicCustomerEndpoint(url)) {
        return Promise.reject(error);
      }

      if (isGuestEndpoint(url)) {
        console.warn(
          "[HTTP] Guest endpoint 401 — guest token may be expired:",
          url,
        );
        return Promise.reject(error);
      }

      if (isUser) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
        emitAuthChanged();
      }

      const isWriteOperation = ["POST", "PUT", "PATCH", "DELETE"].includes(
        method,
      );

      const isSensitiveRead =
        method === "GET" &&
        (url.includes("/orders") ||
          url.includes("/profile") ||
          url.includes("/customer_address"));

      if (isWriteOperation || isSensitiveRead) {
        let message = "Please login to continue";

        if (isGuest) {
          message =
            "This feature requires a full account. Please login to continue.";
        }

        openAuthModalOncePerSession(message);
      }
    }

    return Promise.reject(error);
  },
);

export default http;